// Client side of the fixed-frame preset geometry (C360-3321). The aiming half
// mirrors business-api Services/framingPresets/framingMath.ts, which aims the
// clip when it is saved; this copy aims the preview and draws the target on
// the video.
//
// Player conventions (c360-lead): pan is measured from the camera's forward
// heading, positive turns right; tilt is degrees below the horizon; zoom is
// the vcam's vertical field of view and the image is rectilinear below 60°
// (lead/src/warp/project.rs, no "dive" until then).

import { type AimCamera, type AimPoint, cameraHeading } from '@c360/shared';
import { PTZValues } from './types';

// The aim itself is the API's (libs/shared framingAim), so the preview and
// the export can't disagree.
export { aimAt, wrapDegrees } from '@c360/shared';

export type Vec3 = AimPoint;
export type CameraCalibration = AimCamera;

/** Player preview is 16:9 (FOVOverlay makes the same assumption). */
export const VIDEO_ASPECT = 16 / 9;

const rad = (deg: number) => (deg * Math.PI) / 180;

/** World axes of the vcam: forward, right and up. */
function vcamBasis(cam: CameraCalibration, ptz: PTZValues) {
  const yaw = rad(cameraHeading(cam) - ptz.pan);
  const pitch = rad(-ptz.tilt);
  const forward = {
    x: Math.cos(pitch) * Math.cos(yaw),
    y: Math.cos(pitch) * Math.sin(yaw),
    z: Math.sin(pitch),
  };
  // Increasing pan turns right, and pan decreases the heading, so right is
  // the direction the heading decreases in.
  const right = { x: Math.sin(yaw), y: -Math.cos(yaw), z: 0 };
  const up = {
    x: -Math.sin(pitch) * Math.cos(yaw),
    y: -Math.sin(pitch) * Math.sin(yaw),
    z: Math.cos(pitch),
  };
  return { forward, right, up };
}

const dot = (a: Vec3, b: Vec3) => a.x * b.x + a.y * b.y + a.z * b.z;

// The centre of a frame only names a spot on the ice when it looks down at
// it, and not past the far end of any arena.
const MIN_GROUND_TILT = 1;
const MAX_GROUND_RANGE = 400;

/**
 * The spot on the ice at the centre of the frame: what "save current frame"
 * stores when no target was placed. null when the camera looks at or above
 * the horizon.
 */
export function groundPointAt(cam: CameraCalibration, ptz: PTZValues): Vec3 | null {
  if (ptz.tilt < MIN_GROUND_TILT) return null;
  const { forward } = vcamBasis(cam, ptz);
  const s = -cam.positionZ / forward.z;
  if (!(s > 0) || s * Math.hypot(forward.x, forward.y) > MAX_GROUND_RANGE) return null;
  return { x: cam.positionX + s * forward.x, y: cam.positionY + s * forward.y, z: 0 };
}

/**
 * Where `point` appears in the preview, as fractions of its width and height
 * from the top-left corner. null when it is behind the camera.
 */
export function projectToVideo(
  cam: CameraCalibration,
  ptz: PTZValues,
  point: Vec3,
): { x: number; y: number } | null {
  const { forward, right, up } = vcamBasis(cam, ptz);
  const d = { x: point.x - cam.positionX, y: point.y - cam.positionY, z: point.z - cam.positionZ };
  const depth = dot(d, forward);
  if (depth <= 0.1) return null;
  const halfHeight = Math.tan(rad(ptz.zoom) / 2);
  const ndcX = dot(d, right) / (depth * halfHeight * VIDEO_ASPECT);
  const ndcY = dot(d, up) / (depth * halfHeight);
  return { x: (1 + ndcX) / 2, y: (1 - ndcY) / 2 };
}

/**
 * The height on the vertical line through (x, y) that sits under a point of
 * the preview: what dragging the target up and down on the video sets.
 */
export function heightAtVideoPoint(
  cam: CameraCalibration,
  ptz: PTZValues,
  xy: { x: number; y: number },
  video: { x: number; y: number },
): number | null {
  const { forward, right, up } = vcamBasis(cam, ptz);
  const halfHeight = Math.tan(rad(ptz.zoom) / 2);
  const ndcX = video.x * 2 - 1;
  const ndcY = 1 - video.y * 2;
  const ray = {
    x: forward.x + ndcX * halfHeight * VIDEO_ASPECT * right.x + ndcY * halfHeight * up.x,
    y: forward.y + ndcX * halfHeight * VIDEO_ASPECT * right.y + ndcY * halfHeight * up.y,
    z: forward.z + ndcX * halfHeight * VIDEO_ASPECT * right.z + ndcY * halfHeight * up.z,
  };
  // Closest approach between the ray and the vertical line: along the ray,
  // that is where its horizontal projection is nearest to (x, y).
  const toLine = { x: xy.x - cam.positionX, y: xy.y - cam.positionY };
  const flatSq = ray.x * ray.x + ray.y * ray.y;
  if (flatSq < 1e-9) return null;
  const s = (toLine.x * ray.x + toLine.y * ray.y) / flatSq;
  if (s <= 0) return null;
  return cam.positionZ + s * ray.z;
}

/**
 * The nearest point on the ice to (x, y), in surface feet with the origin at
 * centre ice: inside the boards, and inside the rounded corners when the
 * surface has them. A click on the rink image outside the ice would aim the
 * camera at the boards or the seats behind them.
 */
export function clampToSurface(
  x: number,
  y: number,
  surfaceWidth: number,
  surfaceHeight: number,
  cornerRadius = 0,
): { x: number; y: number } {
  const hw = surfaceWidth / 2;
  const hh = surfaceHeight / 2;
  const cx = Math.min(Math.max(x, -hw), hw);
  const cy = Math.min(Math.max(y, -hh), hh);
  const r = Math.min(cornerRadius, hw, hh);
  // Centre of the corner's arc, when the point is in a corner square.
  const ax = hw - r;
  const ay = hh - r;
  if (r <= 0 || Math.abs(cx) <= ax || Math.abs(cy) <= ay) return { x: cx, y: cy };
  const dx = Math.abs(cx) - ax;
  const dy = Math.abs(cy) - ay;
  const d = Math.hypot(dx, dy);
  if (d <= r) return { x: cx, y: cy };
  return {
    x: Math.sign(cx) * (ax + (dx / d) * r),
    y: Math.sign(cy) * (ay + (dy / d) * r),
  };
}
